import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface FixStatus {
  processId: string;
  filePath: string;
  status: 'running' | 'success' | 'error' | 'cancelled';
  output: string;
  startTime: number;
  endTime?: number;
  gitDiff?: string;
}

interface ActiveFixesStore {
  fixes: Record<string, FixStatus>;

  startFix: (filePath: string, processId: string) => void;
  appendOutput: (filePath: string, chunk: string) => void;
  setStatus: (filePath: string, status: FixStatus['status']) => void;
  setGitDiff: (filePath: string, diff: string) => void;
  getFix: (filePath: string) => FixStatus | undefined;
  removeFix: (filePath: string) => void;
  clearCompleted: () => void;
}

export const useActiveFixesStore = create<ActiveFixesStore>()(
  persist(
    (set, get) => ({
      fixes: {},

      startFix: (filePath, processId) => set((state) => ({
        fixes: {
          ...state.fixes,
          [filePath]: {
            processId,
            filePath,
            status: 'running',
            output: '',
            startTime: Date.now()
          }
        }
      })),

      appendOutput: (filePath, chunk) => set((state) => {
        const fix = state.fixes[filePath];
        if (!fix) return state;
        return {
          fixes: {
            ...state.fixes,
            [filePath]: { ...fix, output: fix.output + chunk }
          }
        };
      }),

      setStatus: (filePath, status) => set((state) => {
        const fix = state.fixes[filePath];
        if (!fix) return state;
        return {
          fixes: {
            ...state.fixes,
            [filePath]: {
              ...fix,
              status,
              endTime: Date.now()
            }
          }
        };
      }),

      setGitDiff: (filePath, diff) => set((state) => {
        const fix = state.fixes[filePath];
        if (!fix) return state;
        return {
          fixes: {
            ...state.fixes,
            [filePath]: { ...fix, gitDiff: diff }
          }
        };
      }),

      getFix: (filePath) => get().fixes[filePath],

      removeFix: (filePath) => set((state) => {
        const { [filePath]: _removed, ...rest } = state.fixes;
        return { fixes: rest };
      }),

      clearCompleted: () => set((state) => {
        const runningFixes: Record<string, FixStatus> = {};
        Object.entries(state.fixes).forEach(([path, fix]) => {
          if (fix.status === 'running') {
            runningFixes[path] = fix;
          }
        });
        return { fixes: runningFixes };
      })
    }),
    { name: 'active-fixes-storage' }
  )
);
